import { useState } from 'react';

import EmailIcon from 'icons/email.svg?react';
import { toast } from 'material-react-toastify';

import Counter from '@/components/Counter/Counter';
import Popup from '@/components/Popup/PopUp/PopUp';
import Button from '@/components/shared/Button/Button';
import { Text } from '@/components/shared/Text/Text';
import { TextField } from '@/components/shared/TextField/TextField';
import { hubApi, withAuthHeader } from '@/lib/backend/api';
import { getAuthToken } from '@/lib/firebase/token';
import { userKeys } from '@/lib/query/keys/user';
import { useHubStore } from '@/lib/store/hubStore';
import { AddChatMinutesHubRequest } from '@addmile/backend-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';

const useAddChatMinutes = () => {
  return useMutation({
    mutationFn: async (data: AddChatMinutesHubRequest) => {
      const token = await getAuthToken();
      return hubApi.addChatMinutesHub(data, withAuthHeader(token));
    },
  });
};

interface AddChatMinutesModalProps {
  isPopupOpen: boolean;
  closePopup: () => void;
}

const AddChatMinutesModal = ({ isPopupOpen, closePopup }: AddChatMinutesModalProps) => {
  const user = useHubStore((state) => state.searchUserData);
  const queryClient = useQueryClient();
  const [minutes, setMinutes] = useState<number>(30);
  const [description, setDescription] = useState<string>('hub');
  const addChatMinutesMutation = useAddChatMinutes();

  const resetAndClose = () => {
    setMinutes(30);
    setDescription('');
    closePopup();
  };

  const handleAddChatMinutes = async () => {
    if (!user?.id) {
      toast.error('User ID is missing');
      return;
    }

    addChatMinutesMutation.mutate(
      {
        userId: user.id,
        minutes,
        description,
      },
      {
        onSuccess: () => {
          resetAndClose();
          queryClient.invalidateQueries({ queryKey: userKeys.chatBalance(user.id) });
          toast.success(`User's expiration date was successfully changed`);
        },
        onError: (error) => {
          resetAndClose();
          toast.error(error.message);
        },
      },
    );
  };

  return (
    <Popup isOpen={isPopupOpen} handleClose={closePopup}>
      <div className="absolute top-[20px] left-[12px] rounded-lg bg-[#F2F2F2] p-2">
        <EmailIcon />
      </div>
      <div className="max-w-[340px] ml-12">
        <Text.Bigger className="mb-2" weight="bold">
          Add chat minutes
        </Text.Bigger>
        <Text.Regular>Please select the number of chat minutes you need to add:</Text.Regular>
        <div className="flex justify-center items-center my-4">
          <Counter
            count={minutes}
            increment={() => setMinutes((prev) => prev + 5)}
            decrement={() => setMinutes((prev) => prev - 5)}
          />
        </div>

        <div className="flex justify-center items-center my-4">
          <TextField
            placeholder="Description"
            value={description}
            isTextArea
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className="flex justify-between items-center gap-2">
          <Button
            label={addChatMinutesMutation.isPending ? 'Updating...' : 'Add Chat Minutes'}
            size="s"
            className="!w-44"
            onClick={handleAddChatMinutes}
            disabled={addChatMinutesMutation.isPending || !minutes}
            prefixIcon={<img alt={'check'} src={'/icons/check.svg'} />}
          />
          <Button
            prefixIcon={<img alt={'close'} src={'/icons/close_outline.svg'} />}
            label="Cancel"
            variant="transparent"
            onClick={resetAndClose}
            className="!w-28"
            size="s"
            disabled={addChatMinutesMutation.isPending}
          />
        </div>
      </div>
    </Popup>
  );
};

export default AddChatMinutesModal;
